'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Gift, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { currencies } from '@/lib/types';
import {
  CustomerSurface,
  CustomerSurfaceBody,
  CustomerSurfaceHeader,
} from '@/components/customer-surface/CustomerSurface';

const PRESETS = [25, 50, 100, 200];

export default function GiftCardBuyForm({
  slug,
  businessName,
  currency,
}: {
  slug: string;
  businessName: string | null;
  currency: string | null;
}) {
  const router = useRouter();
  const symbol = currencies.find((c) => c.code === currency)?.symbol || '';

  const [amount, setAmount] = useState<string>('50');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const numericAmount = Number.parseFloat(amount) || 0;

  const buy = async () => {
    setError(null);
    if (numericAmount <= 0) {
      setError('Enter an amount greater than zero.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/public/gift-cards/purchase/${slug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ denomination: numericAmount, recipientPhone: recipientPhone.trim() || undefined }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Could not start this purchase.');
      router.push(data.payUrl as string);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start this purchase.');
      setSubmitting(false);
    }
  };

  // Was a bare `mx-auto max-w-md p-6` div — no header, no identity, no way back.
  // A guest buying a gift card is about to be sent to a payment provider, and
  // this was the last screen that could tell them whose gift card it is.
  return (
    <CustomerSurface brand={{ businessName }}>
      <CustomerSurfaceHeader
        brand={{ businessName }}
        eyebrow="Gift cards"
        title={businessName ? `${businessName} gift card` : 'Buy a gift card'}
      />
      <CustomerSurfaceBody width="narrow">
      <Card>
        <CardHeader className="items-center text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
            <Gift className="h-5 w-5" />
          </span>
          <CardTitle>{businessName ? `${businessName} gift card` : 'Buy a gift card'}</CardTitle>
          <CardDescription>Choose an amount and pay , the recipient can redeem it in-store.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>Amount</Label>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setAmount(String(preset))}
                  className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                    numericAmount === preset
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-input hover:bg-accent'
                  }`}
                >
                  {symbol}{preset}
                </button>
              ))}
            </div>
            <div className="relative">
              {symbol && (
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
                  {symbol}
                </span>
              )}
              <Input
                type="number"
                min={1}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={symbol ? 'pl-7' : ''}
                placeholder="Custom amount"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="gc-recipient">Recipient phone (optional)</Label>
            <Input
              id="gc-recipient"
              value={recipientPhone}
              onChange={(e) => setRecipientPhone(e.target.value)}
              placeholder="So they can be notified"
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <Button className="w-full" disabled={submitting || numericAmount <= 0} onClick={() => void buy()}>
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Gift className="mr-2 h-4 w-4" />}
            {numericAmount > 0 ? `Buy ${symbol}${numericAmount.toFixed(2)} gift card` : 'Buy gift card'}
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            You'll pay securely on the next screen. The card is issued once payment is confirmed.
          </p>
        </CardContent>
      </Card>
      </CustomerSurfaceBody>
    </CustomerSurface>
  );
}
